import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageSquare, ArrowRight, CheckCircle2, Shield, Clock, MapPin } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import ContactSection from '../ContactSection';
import Seo from '../../seo/Seo';
import Breadcrumbs from './Breadcrumbs';
import FAQAccordion from './FAQAccordion';
import Gallery from './Gallery';
import { SITE, buildLocalBusinessJsonLd, buildServiceJsonLd, buildFaqJsonLd, buildBreadcrumbJsonLd } from '../../seo/siteMeta';

/**
 * Shared layout for every service page. Provides:
 *  - SEO meta + JSON-LD (LocalBusiness, Service, FAQPage, BreadcrumbList)
 *  - Hero with click-to-call + WhatsApp
 *  - Body content slot (children)
 *  - Service-area chips
 *  - "Why H.A.R.P" trust strip
 *  - Image gallery (empty until real photos uploaded)
 *  - FAQ accordion
 *  - Internal-link grid to other service pages
 *  - Contact form (re-uses existing /api/contact/submit)
 *
 * Each service page passes its own copy via props; layout enforces consistency.
 */
const ServicePageLayout = ({
  // SEO
  seoTitle,
  seoDescription,
  canonicalPath,
  keywords,
  // Page content
  pageTitle, // H1
  intro, // string or JSX — short paragraph below H1
  serviceName, // human-readable, e.g. "Plumbing Services"
  serviceSlug, // url-safe, e.g. "plumbing"
  heroSubheading,
  bullets = [], // ["Fully insured", "Same-day response", ...]
  children, // long-form body content
  faqs = [],
  relatedServices = [], // [{ slug, label, blurb }]
  galleryImages = [],
  galleryTitle = 'Recent Work',
}) => {
  const breadcrumbItems = [
    { label: 'Home', url: '/' },
    { label: 'Services', url: '/#services' },
    { label: serviceName, url: canonicalPath },
  ];

  const jsonLd = [
    buildLocalBusinessJsonLd(),
    buildServiceJsonLd({ name: serviceName, slug: serviceSlug, description: seoDescription }),
    buildFaqJsonLd(faqs),
    buildBreadcrumbJsonLd(breadcrumbItems),
  ];

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalPath}
        keywords={keywords}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="bg-gray-950 text-white pt-24" data-testid={`service-page-${serviceSlug}`}>
        {/* Hero */}
        <section className="border-b border-gray-900 bg-gradient-to-b from-gray-950 via-gray-950 to-gray-900/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <Breadcrumbs items={breadcrumbItems} />
            <div className="mt-6 grid lg:grid-cols-5 gap-10 items-start">
              <div className="lg:col-span-3">
                <h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight text-white"
                  data-testid="service-h1"
                >
                  {pageTitle}
                </h1>
                {heroSubheading && (
                  <p className="mt-4 text-lg text-amber-500 font-medium">{heroSubheading}</p>
                )}
                {intro && (
                  <p className="mt-5 text-gray-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                    {intro}
                  </p>
                )}

                {bullets.length > 0 && (
                  <ul className="mt-6 grid sm:grid-cols-2 gap-2 max-w-2xl">
                    {bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-200">
                        <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={`tel:${SITE.phoneTel}`}
                    className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-md font-semibold transition-colors"
                    data-testid="service-call-cta"
                  >
                    <Phone className="w-4 h-4" /> Call {SITE.phone}
                  </a>
                  <a
                    href="#service-contact"
                    className="inline-flex items-center justify-center gap-2 border border-gray-700 hover:border-amber-500/60 text-white px-6 py-3 rounded-md font-medium transition-colors"
                    data-testid="service-quote-cta"
                  >
                    Request a Quote <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Trust strip */}
              <aside className="lg:col-span-2 rounded-xl border border-gray-800 bg-gray-900/60 p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Fully Insured</p>
                    <p className="text-gray-400 text-sm">Public liability insured engineers, every job documented.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">24/7 Emergency</p>
                    <p className="text-gray-400 text-sm">Burst pipes and major leaks answered any time, day or night.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Local to You</p>
                    <p className="text-gray-400 text-sm">Engineers based across Kent, London and Essex for fast response.</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-800 flex items-center gap-2 text-sm text-gray-400">
                  <MessageSquare className="w-4 h-4 text-emerald-500" />
                  <span>Prefer WhatsApp? Tap the green button at the bottom of the page.</span>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="border-b border-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 prose prose-invert prose-amber max-w-none">
            {children}
          </div>
        </section>

        {/* Gallery */}
        <section className="border-b border-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <Gallery serviceSlug={serviceSlug} images={galleryImages} title={galleryTitle} />
          </div>
        </section>

        {/* FAQ */}
        {faqs.length > 0 && (
          <section className="border-b border-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">Frequently Asked Questions</h2>
              <p className="text-gray-400 mb-8">Common questions about our {serviceName.toLowerCase()}.</p>
              <FAQAccordion faqs={faqs} />
            </div>
          </section>
        )}

        {/* Related services */}
        {relatedServices.length > 0 && (
          <section className="border-b border-gray-900 bg-gray-900/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">Related Services</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedServices.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/services/${r.slug}`}
                    className="group block rounded-lg border border-gray-800 hover:border-amber-500/50 bg-gray-900/40 p-5 transition-colors"
                    data-testid={`related-${r.slug}`}
                  >
                    <p className="text-white font-semibold group-hover:text-amber-500">{r.label}</p>
                    <p className="text-sm text-gray-400 mt-1">{r.blurb}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact form — reuses existing CRM-connected ContactSection */}
        <div id="service-contact">
          <ContactSection initialService={serviceSlug === 'emergency-plumber' ? 'emergency' : ['plumbing', 'handyman', 'air-conditioning', 'refrigeration'].includes(serviceSlug) ? serviceSlug : undefined} />
        </div>
      </main>

      <Footer />
    </>
  );
};

export default ServicePageLayout;
